import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { usePageTitle } from '../../../lib/hooks';
import { PageHeader } from '../../../components/layout/PageHeader';
import { PageSection } from '../../../components/layout/PageSection';
import { defineListPage } from '@repo/list';
import { DataTable } from '@repo/web-list';
import { Money } from '../../../components/ui/Money';
import { useState } from 'react';
import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from '../../../lib/locale';
import { useAuctionsList, type AuctionListItem } from '../../../features/auctions/network';
import type { UseListPageReturn } from '@repo/list';

export const Route = createFileRoute('/_auth/auctions/')({
  component: AuctionDashboardPage,
});

const auctionConfig = defineListPage<AuctionListItem>({
  variant: 'data-table',
  resource: 'auctions',
  columns: [
    { id: 'schemeCode', header: 'Scheme Code', accessor: 'schemeCode' as const, renderType: 'text' },
    { id: 'auctionNo', header: 'Auction No', accessor: 'auctionNo' as const, renderType: 'numeric', numeric: true },
    { id: 'date', header: 'Date', accessor: 'date' as const, renderType: 'date' },
    { id: 'status', header: 'Status', accessor: 'status' as const, renderType: 'status', renderMeta: { colorMap: { active: 'success', upcoming: 'warning', completed: 'info' } } },
    { id: 'baseAmount', header: 'Base Amount', accessor: 'baseAmount' as const, renderType: 'money', numeric: true },
    { id: 'winner', header: 'Winner / Max Bid', accessor: 'winner' as const, renderType: 'text' },
  ],
  defaultPageSize: 10,
});

function makeListState(data: AuctionListItem[], isLoading: boolean): UseListPageReturn<AuctionListItem> {
  return {
    data,
    meta: { total: data.length, page: 1, limit: data.length || 10 },
    isLoading,
    isError: false,
    error: null,
    refetch: () => {},
    page: 1,
    setPage: () => {},
    search: '',
    setSearch: () => {},
    sort: null,
    setSort: () => {},
    filters: {},
    setFilter: () => {},
    resetFilters: () => {},
    resetAll: () => {},
  };
}

const winnerRenderer = (row: AuctionListItem) => {
  if (row.status === 'completed') {
    return (
      <div className="text-sm">
        <div>{row.winner}</div>
        <div className="text-text-secondary mt-0.5">
          <Money paise={row.bidAmount || 0} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
        </div>
      </div>
    );
  }
  return <span className="text-text-secondary">-</span>;
};

function AuctionDashboardPage() {
  usePageTitle('Auction Dashboard');
  const { data: auctionsResponse, isLoading } = useAuctionsList();
  const allAuctions = auctionsResponse?.data ?? [];

  const activeAuctions = allAuctions.filter(a => a.status === 'active');
  const upcomingAuctions = allAuctions.filter(a => a.status === 'upcoming');
  const completedAuctions = allAuctions.filter(a => a.status === 'completed');

  return (
    <div className="space-y-6">
      <PageHeader
        title="Auction Dashboard"
        subtitle="Global view of upcoming, active, and completed auctions across all schemes."
      />
      <div className="flex flex-col gap-8 pb-8 mt-6">
        <PageSection title="Active Auctions">
          <DataTable
            config={auctionConfig}
            list={makeListState(activeAuctions, isLoading)}
            customRenderers={{ winner: winnerRenderer }}
          />
        </PageSection>

        <PageSection title="Upcoming Auctions">
          <DataTable
            config={auctionConfig}
            list={makeListState(upcomingAuctions, isLoading)}
            customRenderers={{ winner: winnerRenderer }}
          />
        </PageSection>

        <PageSection title="Completed Auctions">
          <DataTable
            config={auctionConfig}
            list={makeListState(completedAuctions, isLoading)}
            customRenderers={{ winner: winnerRenderer }}
          />
        </PageSection>
      </div>
    </div>
  );
}
